/**
 * jobNotes.js — the list the job's Notes tab shows.
 *
 * IDI-551 (BUILD CONTRACT 2026-10-21, supersedes IDI-522 items 1 + 4): a
 * converted job's Notes tab lists the job's own notes plus the notes written
 * on its quote, newest first. The quote's notes come from the job GET's
 * `quoteNotes` (read on the server when the job is viewed, never copied into
 * job.notes), so jobs converted before this shipped and field techs see them
 * too. Each quote note is labelled "Quote Q-0332" with no link (IDI-157) and
 * is read-only here (edit/delete is IDI-552).
 *
 * Work-order notes are NOT listed: they show on their work order only
 * (Sarah L601-625; Lindsey L615).
 *
 * Each entry keeps the stored author (never 'You'; IDI-305). An author-less
 * entry falls back to NotesPanel's "Earlier note (author not recorded)".
 */
import { formatQuoteNumber } from './displayId';

function timeOf(n) {
  // IDI-552: an edited note is placed by its edit time.
  const t = Date.parse(n.updated_at || n.created_at || '');
  return Number.isNaN(t) ? 0 : t;
}

function cleanAuthor(a) {
  const s = String(a || '').trim();
  return /^you$/i.test(s) ? '' : s;
}

function normaliseNote(n, idx, prefix) {
  if (!n || typeof n !== 'object') return null;
  const content = n.content || n.text || '';
  return {
    ...n,
    id: n.id || `${prefix}-${idx}`,
    content,
    // IDI-305: a stored 'You' (pre-PT-40.7 notes) names nobody; never show it.
    author: cleanAuthor(n.author || n.author_name || n.authorName || ''),
    author_id: n.author_id || n.authorId || null,
    created_at: n.created_at || n.createdAt || '',
    updated_at: n.updated_at || n.updatedAt || '',
  };
}

/** "Quote Q-0332" from the job's stored quote number; "Quote" when it has none. */
export function quoteNoteLabel(job) {
  const num = formatQuoteNumber({
    quoteNumber: job?.quoteNumber,
    quote_number: job?.quote_number,
  });
  return num && num !== '—' ? `Quote ${num}` : 'Quote';
}

/**
 * Merge a job's own notes with its converted quote's notes, newest first
 * (stable: equal times keep job notes, then quote notes, in stored order).
 * @param {Array} jobNotes   job.notes
 * @param {Array} quoteNotes job.quoteNotes (server-computed)
 * @param {object} job       the job, for the quote's display number
 * @returns {Array<{id, content, author, created_at, source: 'job'|'quote', sourceLabel?}>}
 */
export function mergeJobNotes(jobNotes, quoteNotes, job) {
  const out = [];
  (Array.isArray(jobNotes) ? jobNotes : []).forEach((n, i) => {
    const note = normaliseNote(n, i, 'job-note');
    if (note) out.push({ ...note, source: 'job' });
  });
  const label = quoteNoteLabel(job);
  (Array.isArray(quoteNotes) ? quoteNotes : []).forEach((n, i) => {
    const note = normaliseNote(n, i, 'quote-note');
    if (!note || !note.content) return;
    out.push({
      ...note,
      // Keep React keys unique even if a quote note reuses a job note's id.
      id: `quote:${note.id}`,
      noteId: note.id,
      source: 'quote',
      // IDI-552: edit/delete of a quote row goes to the quote's endpoints.
      quoteId: job?.quoteId || job?.quote_id || null,
      sourceLabel: label,
    });
  });
  return out
    .map((n, i) => ({ n, i }))
    .sort((a, b) => timeOf(b.n) - timeOf(a.n) || a.i - b.i)
    .map(({ n }) => n);
}

export default mergeJobNotes;
